import { useState } from "react";
import {
  AnalisisEstudio,
  AnalisisEstudioPropiedad,
  EntradaBitacoraAnalisis,
  Documento,
  Holding,
  Cliente,
  Propiedad,
  Usuario,
  Caso,
} from "../../types";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { Badge } from "../common/Badge";
import { AnalisisForm } from "./AnalisisForm";
import { AnalisisDetail } from "./AnalisisDetail";

function getEstadoAnalisisBadgeVariant(estado: string) {
  const variants: Record<string, "neutral" | "info" | "success" | "error"> = {
    creado: "neutral",
    en_estudio: "info",
    completado: "success",
    desistido: "error",
  };
  return variants[estado] || "neutral";
}

interface AnalisisViewProps {
  analisisEstudios: AnalisisEstudio[];
  analisisEstudioPropiedades: AnalisisEstudioPropiedad[];
  bitacoraAnalisis: EntradaBitacoraAnalisis[];
  documentos: Documento[];
  holdings: Holding[];
  clientes: Cliente[];
  propiedades: Propiedad[];
  usuarios: Usuario[];
  usuarioActual: Usuario;
  onAnalisisCreate: (data: Omit<AnalisisEstudio, "id" | "fechaCreacion" | "fechaActualizacion">) => number;
  onAnalisisUpdate: (analisis: AnalisisEstudio) => void;
  onAnalisisDelete: (id: number) => void;
  onAnalisisPropiedadCreate: (data: Omit<AnalisisEstudioPropiedad, "id">) => void;
  onAnalisisPropiedadUpdate: (aep: AnalisisEstudioPropiedad) => void;
  onAnalisisPropiedadDelete: (id: number) => void;
  onBitacoraAnalisisCreate: (data: Omit<EntradaBitacoraAnalisis, "id" | "fechaCreacion">) => void;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
  onDocumentoDelete: (id: number) => void;
  onCrearSolicitudDesdeAnalisis: (analisisId: number, propiedadIds: number[]) => void;
  casos: Caso[];
}

export function AnalisisView({
  analisisEstudios,
  analisisEstudioPropiedades,
  bitacoraAnalisis,
  documentos,
  holdings,
  clientes,
  propiedades,
  usuarios,
  usuarioActual,
  onAnalisisCreate,
  onAnalisisUpdate,
  onAnalisisDelete,
  onAnalisisPropiedadCreate,
  onAnalisisPropiedadUpdate,
  onAnalisisPropiedadDelete,
  onBitacoraAnalisisCreate,
  onDocumentoCreate,
  onDocumentoDelete,
  onCrearSolicitudDesdeAnalisis,
  casos,
}: AnalisisViewProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState<string>("activos");
  const [filterHolding, setFilterHolding] = useState<number | "all">("all");
  const [selectedAnalisis, setSelectedAnalisis] = useState<AnalisisEstudio | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [analisisToDelete, setAnalisisToDelete] = useState<AnalisisEstudio | null>(null);

  const getHoldingNombre = (holdingId: number) =>
    holdings.find((h) => h.id === holdingId)?.nombre || "-";

  const filteredAnalisis = analisisEstudios.filter((a) => {
    const holdingNombre = getHoldingNombre(a.holdingId);
    const matchesSearch =
      a.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holdingNombre.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesEstado = false;
    if (filterEstado === "all") {
      matchesEstado = true;
    } else if (filterEstado === "activos") {
      matchesEstado = !["desistido", "completado"].includes(a.estado);
    } else {
      matchesEstado = a.estado === filterEstado;
    }

    const matchesHolding = filterHolding === "all" || a.holdingId === filterHolding;

    return matchesSearch && matchesEstado && matchesHolding;
  });

  const handleFormSubmit = (data: {
    holdingId: number;
    titulo: string;
    descripcion?: string;
    arquitectoId?: number;
    abogadoId?: number;
    propiedadIds: number[];
  }) => {
    const { propiedadIds, ...analisisData } = data;
    const newId = onAnalisisCreate({
      ...analisisData,
      estado: "creado",
    });

    // Create junction records for each selected propiedad
    propiedadIds.forEach((propiedadId) => {
      onAnalisisPropiedadCreate({
        analisisEstudioId: newId,
        propiedadId,
        resultado: "pendiente",
      });
    });

    setIsFormOpen(false);
  };

  return (
    <div className="flex h-full">
      {/* Left panel - List */}
      <div className="w-2/5 border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Analysis & Estudios</h2>
            <button
              onClick={() => setIsFormOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Nuevo Estudio
            </button>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              placeholder="Buscar por titulo o holding..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="flex gap-2">
              <select
                value={filterHolding}
                onChange={(e) =>
                  setFilterHolding(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los Holdings</option>
                {holdings.map((h) => (
                  <option key={h.id} value={h.id}>{h.nombre}</option>
                ))}
              </select>

              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="activos">Activos</option>
                <option value="all">Todos</option>
                <option value="creado">Creados</option>
                <option value="en_estudio">En Estudio</option>
                <option value="completado">Completados</option>
                <option value="desistido">Desistidos</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {filteredAnalisis.map((analisis) => {
              const aeps = analisisEstudioPropiedades.filter(
                (aep) => aep.analisisEstudioId === analisis.id
              );
              const aprobados = aeps.filter((a) => a.resultado === "aprobado_para_solicitud").length;
              const pendientes = aeps.filter((a) => a.resultado === "pendiente").length;

              return (
                <div
                  key={analisis.id}
                  onClick={() => setSelectedAnalisis(analisis)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedAnalisis?.id === analisis.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-800 truncate">
                          {analisis.titulo}
                        </h3>
                        <Badge variant={getEstadoAnalisisBadgeVariant(analisis.estado)}>
                          {analisis.estado.replace("_", " ")}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {getHoldingNombre(analisis.holdingId)}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span>{aeps.length} ROLes</span>
                        {aprobados > 0 && (
                          <span className="text-green-600">{aprobados} aprobados</span>
                        )}
                        {pendientes > 0 && (
                          <span>{pendientes} pendientes</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnalisisToDelete(analisis);
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}

            {filteredAnalisis.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                {searchTerm || filterEstado !== "activos" || filterHolding !== "all"
                  ? "No se encontraron estudios"
                  : "No hay estudios registrados"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right panel - Detail */}
      <div className="w-3/5 overflow-y-auto">
        {selectedAnalisis ? (
          <AnalisisDetail
            analisis={selectedAnalisis}
            holding={holdings.find((h) => h.id === selectedAnalisis.holdingId)!}
            clientes={clientes}
            propiedades={propiedades}
            analisisPropiedades={analisisEstudioPropiedades.filter(
              (aep) => aep.analisisEstudioId === selectedAnalisis.id
            )}
            bitacora={bitacoraAnalisis.filter(
              (b) => b.analisisEstudioId === selectedAnalisis.id
            )}
            documentos={documentos}
            usuarios={usuarios}
            usuarioActual={usuarioActual}
            onAnalisisUpdate={(updated) => {
              onAnalisisUpdate(updated);
              setSelectedAnalisis(updated);
            }}
            onAnalisisPropiedadUpdate={onAnalisisPropiedadUpdate}
            onBitacoraCreate={onBitacoraAnalisisCreate}
            onDocumentoCreate={onDocumentoCreate}
            onDocumentoDelete={onDocumentoDelete}
            onCrearSolicitudDesdeAnalisis={onCrearSolicitudDesdeAnalisis}
            casos={casos}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            Selecciona un estudio para ver detalles
          </div>
        )}
      </div>

      {/* Modal Form */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Nuevo Estudio"
        size="lg"
      >
        <AnalisisForm
          holdings={holdings}
          clientes={clientes}
          propiedades={propiedades}
          usuarios={usuarios}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormOpen(false)}
        />
      </Modal>

      {/* Confirm Delete */}
      <ConfirmDialog
        isOpen={analisisToDelete !== null}
        onClose={() => setAnalisisToDelete(null)}
        onConfirm={() => {
          if (analisisToDelete) {
            onAnalisisDelete(analisisToDelete.id);
            if (selectedAnalisis?.id === analisisToDelete.id) {
              setSelectedAnalisis(null);
            }
          }
        }}
        title="Eliminar Estudio"
        message={`Esta seguro que desea eliminar el estudio "${analisisToDelete?.titulo}"? Se eliminaran la bitacora y documentos asociados.`}
        confirmText="Eliminar"
      />
    </div>
  );
}
